/**
 * Express cart lines backed by a session's real cart (see
 * lib/express-cart/session-cart.ts).
 *
 * - With a session (the agent's View Cart link, or a brand link the browser
 *   already has a session for), edits are saved to it straight away. This also
 *   adds a brand link's products to that cart. Magento brands are the
 *   exception: their cart is only written at "Continue to checkout".
 * - On a brand link without one, nothing is created on page view: the first
 *   edit that needs saving, or "Continue", creates the session.
 * - Saving goes through POST /update-cart, debounced and one sync after
 *   another. Each call sends one row: the next change depends on how the last
 *   one changed the cart (a SKU can turn a row without a variant into that
 *   variant), so they aren't batched.
 * - The link's items stay on the page at 0; other cart items disappear once
 *   removed. "Continue" finishes saving, then opens the brand's
 *   Shopify cart (GET /shopify-checkout) or the Nile checkout
 *   (/order/:sessionId), which starts on shipping.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { draftApi, expressCartApi } from "@/api/client";
import { cacheSessionResponse } from "@/api/session-cache";
import type { ExpressCartController } from "@/components/express-cart/ExpressCart";
import type { CheckoutLine, CheckoutLink } from "@/lib/express-cart/checkout";
import { saveExpressCartReturn, saveExpressSession, saveMagentoCartIds, saveSessionCartToken } from "@/lib/express-cart/device";
import { addMagentoCartItem, createMagentoCart, removeMagentoCartItem, seedMagentoCartRegistry, updateMagentoCartItem, type MagentoCartRegistry } from "@/lib/magento-helper";
import { linkSource } from "@/lib/express-cart/link-source";
import { trackCartAdd, trackCartRemove, trackCheckoutError } from "@/lib/analytics";
import { attachCartItemIds, dropRemovedLines, linesFromCart, nextCartChange, rowVariant } from "@/lib/express-cart/session-cart";
import type { CartItem, CheckoutUpdateCartResult } from "@/types";
import {
  buildProductVerificationGroups,
  verifyProductGroups,
  type ProductVerificationResult,
} from "@/lib/product-verification";
import {
  fetchIntegrationConfig,
  fetchMarketplaceSession,
} from "@/lib/supabase-api";
import {
  automaticDiscountsFromDraft,
  draftOrderFailed,
  saveDraftDiscountHandoff,
} from "@/lib/express-cart/draft-discounts";

const SYNC_DELAY_MS = 600;
/** More single-row calls than any one sync should need; guards against a loop. */
const MAX_CHANGES_PER_SYNC = 50;
const MISSING_BRAND_ID_ERROR = "This item is missing its fulfilment brand. Please reload your cart or contact support.";
const MULTIPLE_BRAND_CARTS_ERROR = "This brand has multiple Magento carts linked to your checkout. Please contact support before continuing.";

export interface CartSaveFailure {
  productId: string;
  variant: string | null;
  message: string;
}

function cartRowKey(productId: string, variant: string | null): string {
  return `${productId}|${variant ?? ""}`;
}

class MagentoCartSaveError extends Error {
  constructor(
    message: string,
    readonly productId: string,
    readonly variant: string | null,
  ) {
    super(message);
    this.name = "MagentoCartSaveError";
  }
}

/**
 * Creates the brand's Magento cart and returns its ID. If a create is
 * already running for the same brand (the page-load create and a shopper
 * edit can both need one), it waits for that instead of making a second
 * cart. A failed create is removed from the map so the next caller can
 * try again.
 */
function getOrCreateBrandMagentoCart(
  registry: MagentoCartRegistry,
  creating: Map<string, Promise<string>>,
  targetRetailerId: string,
  sessionToken: string,
): Promise<string> {
  const inFlight = creating.get(targetRetailerId);
  if (inFlight) return inFlight;
  const created = createMagentoCart(targetRetailerId, sessionToken)
    .then(function (cartId: string) {
      registry.cartIds.set(targetRetailerId, cartId);
      return cartId;
    })
    .finally(function () {
      creating.delete(targetRetailerId);
    });
  creating.set(targetRetailerId, created);
  return created;
}

/** A shopper-facing reason for a row POST /update-cart couldn't change. */
function updateFailure(result: CheckoutUpdateCartResult | undefined): string {
  switch (result?.error) {
    case "insufficient_stock":
      return result.available !== undefined
        ? `only ${result.available} of that item ${result.available === 1 ? "is" : "are"} in stock`
        : "there isn't enough stock";
    case "variant_required":
      return "choose the options for that item first";
    case "variant_not_found":
      return "that option isn't available any more";
    case "product_not_found":
      return "that product isn't available any more";
    default:
      return "please try again";
  }
}

export interface CartSession {
  session_id: string;
  session_token: string;
}

interface Options {
  /** The session to edit, if there already is one. */
  session: CartSession | null;
  /** For a brand link without a session: creates (and stores) one. */
  createSession?: () => Promise<CartSession>;
  /** Where "Continue" goes: the brand's Shopify cart, or the Nile checkout. */
  checkout: "shopify" | "nile";
  retailerId: string;
  integrationType: string | null;
  /** Marketplace child-brand integrations keyed by retailer ID. */
  marketplaceIntegrations?: Record<string, string>;
}

export function useCartSession(
  link: CheckoutLink,
  initialItems: CartItem[],
  { session, createSession, checkout, retailerId, integrationType, marketplaceIntegrations = {} }: Options,
): ExpressCartController & {
  syncError: string | null;
  cartSaveFailures: CartSaveFailure[];
  verification: ProductVerificationResult | null;
  isVerifying: boolean;
  isVerificationPending: boolean;
  requiresVerification: boolean;
  hasSession: boolean;
  liveStockBySku: Map<string, number>;
  livePricesBySku: Map<string, number>;
  session: CartSession | null;
} {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [lines, setLinesState] = useState<CheckoutLine[]>(() => linesFromCart(link, initialItems));
  const [cartRevision, setCartRevision] = useState(0);
  const [magentoRegistry] = useState(() => seedMagentoCartRegistry(initialItems));
  const initialMagentoConflict = magentoRegistry.conflictingBrands.size > 0;
  const [syncError, setSyncError] = useState<string | null>(
    initialMagentoConflict ? MULTIPLE_BRAND_CARTS_ERROR : null,
  );
  const [cartSaveFailures, setCartSaveFailures] = useState<CartSaveFailure[]>([]);
  const [activeSession, setActiveSession] = useState<CartSession | null>(session);
  const [verification, setVerification] = useState<ProductVerificationResult | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const requiresVerification = ["cumi", "magento"].includes(integrationType?.toLowerCase() ?? "");
  // Magento stock is verified once, when the shopper continues to checkout;
  // cumi keeps the per-edit live verification.
  const isMagentoIntegration = integrationType?.toLowerCase() === "magento";
  const [isVerificationPending, setIsVerificationPending] = useState(
    Boolean(session && requiresVerification && !isMagentoIntegration),
  );

  const sessionRef = useRef<CartSession | null>(session);
  const creatingRef = useRef<Promise<CartSession> | null>(null);
  const verificationRunRef = useRef(0);
  const continueVerificationRef = useRef(false);
  const cartItemsRef = useRef<CartItem[]>(initialItems);
  const syncChainRef = useRef<Promise<void>>(Promise.resolve());
  const editedRef = useRef(false);
  const missingBrandReportsRef = useRef(new Set<string>());
  useEffect(() => {
    console.info("[Magento cart] initial rows have brand cart ID", {
      hasBrandCartId: magentoRegistry.cartIds.size > 0,
    });
    if (magentoRegistry.conflictingBrands.size > 0) {
      Sentry.captureException(new Error(MULTIPLE_BRAND_CARTS_ERROR), {
        tags: { component: "useCartSession", action: "conflictingMagentoCarts" },
        extra: { conflictingBrandCount: magentoRegistry.conflictingBrands.size },
      });
    }
  }, [magentoRegistry]);
  // Keep remote Magento side effects recorded until their matching NILE row save succeeds.
  const completedMagentoChangesRef = useRef(new Map<string, {
    cartId: string;
    itemId?: number;
    removedItemIds: Set<string>;
    mutationDone: boolean;
  }>());
  // Magento cart creates that are already running, one per brand. Shared by
  // the page-load create and the shopper edit path (see getOrCreateBrandMagentoCart).
  const magentoCartCreateRef = useRef(new Map<string, Promise<string>>());
  // One create attempt on page load; the edit path stays the fallback.
  const triedPageLoadCartCreateRef = useRef(false);
  useEffect(() => {
    if (triedPageLoadCartCreateRef.current) return;
    if (integrationType?.toLowerCase() !== "magento") return;
    if (magentoRegistry.conflictingBrands.has(retailerId)) return;
    if (magentoRegistry.cartIds.has(retailerId)) return;
    // A brand link viewed without a session creates nothing on page load;
    // the first edit creates the session, then the cart.
    const currentSession = sessionRef.current;
    if (!currentSession) return;
    triedPageLoadCartCreateRef.current = true;
    getOrCreateBrandMagentoCart(magentoRegistry, magentoCartCreateRef.current, retailerId, currentSession.session_token)
      .catch((error: unknown) => {
        console.info("[useCartSession - pageLoadCartCreate] -", {
          retailerId,
          message: error instanceof Error ? error.message : "Magento create-cart failed",
        });
        trackCheckoutError(
          currentSession.session_id,
          "magento_cart_creation",
          error instanceof Error && error.message ? error.message : "Magento cart creation failed",
          { retailer_id: retailerId },
        );
        // Same message the shopper sees when saving an item to the cart fails;
        // a later successful edit clears it.
        setSyncError(`We couldn't save your cart: ${
          error instanceof Error && error.message
            ? error.message
            : "The item couldn't be saved to the retailer's cart."
        }`);
      });
  }, [integrationType, magentoRegistry, retailerId]);
  const marketplaceIntegrationsRef = useRef(marketplaceIntegrations);

  const createCartSession = useCallback(async (): Promise<CartSession> => {
    if (createSession) return createSession();
    if (!retailerId || !link.code) {
      throw new Error("This cart doesn't have a session yet. Reopen the cart link and try again.");
    }
    const created = await expressCartApi.createSession({
      retailerId,
      linkSlug: link.code,
    });
    if (link.code) saveExpressSession(link.code, created);
    return {
      session_id: created.session_id,
      session_token: created.session_token,
    };
  }, [createSession, link.code, retailerId]);

  const ensureSession = useCallback((): Promise<CartSession> => {
    if (sessionRef.current) return Promise.resolve(sessionRef.current);
    if (!creatingRef.current) {
      creatingRef.current = createCartSession()
        .then((created) => {
          sessionRef.current = created;
          setActiveSession(created);
          return created;
        })
        .catch((error: unknown) => {
          creatingRef.current = null;
          throw error;
        });
    }
    return creatingRef.current;
  }, [createCartSession]);

  /** Queues a sync of these lines after any sync already running. */
  const queueSync = useCallback(
    (target: CheckoutLine[]): Promise<void> => {
      const run = syncChainRef.current.then(async () => {
        if (magentoRegistry.conflictingBrands.size > 0) {
          throw new Error(MULTIPLE_BRAND_CARTS_ERROR);
        }
        let previous: string | null = null;
        for (let step = 0; step < MAX_CHANGES_PER_SYNC; step += 1) {
          const change = nextCartChange(link, target, cartItemsRef.current);
          if (!change) {
            setCartSaveFailures((current) =>
              current.filter((failure) => {
                const matchingLine = target.find((line) => line.productId === failure.productId);
                const stillInCart = cartItemsRef.current.some(
                  (item) =>
                    item.product_id === failure.productId &&
                    rowVariant(item) === failure.variant,
                );
                return Boolean(matchingLine?.quantity) || stillInCart;
              }),
            );
            return;
          }
          const signature = JSON.stringify(change);
          if (signature === previous) throw new Error("Your cart didn't update. Please try again.");
          previous = signature;

          const { session_id, session_token } = await ensureSession();
          if (Object.keys(marketplaceIntegrationsRef.current).length === 0) {
            const marketplace = await fetchMarketplaceSession(session_id, session_token).catch(() => null);
            marketplaceIntegrationsRef.current = Object.fromEntries(
              (marketplace?.retailers ?? []).map((retailer) => [
                retailer.retailer_id,
                retailer.integration_type?.toLowerCase() ?? "",
              ]),
            );
          }
          const product = link.products.find((candidate) => candidate.id === change.productId);
          if (!product) throw new Error("We couldn't find that product. Please reload your cart.");
          const targetRetailerId = product.fulfillment_brand_id || product.retailer_id || retailerId;
          const targetIntegration = marketplaceIntegrationsRef.current[targetRetailerId] ?? integrationType;
          const isMagento = targetIntegration?.toLowerCase() === "magento";
          if (isMagento && (!product.fulfillment_brand_id && !product.retailer_id)) {
            const error = new Error(MISSING_BRAND_ID_ERROR);
            if (!missingBrandReportsRef.current.has(change.productId)) {
              missingBrandReportsRef.current.add(change.productId);
              Sentry.captureException(error, {
                tags: { component: "useCartSession", action: "missingFulfillmentBrandId" },
                extra: { productId: change.productId },
              });
            }
            throw error;
          }
          if (isMagento && magentoRegistry.conflictingBrands.has(targetRetailerId)) {
            throw new Error(MULTIPLE_BRAND_CARTS_ERROR);
          }
          const currentItem = cartItemsRef.current.find(
            (item) =>
              item.product_id === change.productId &&
              rowVariant(item) === change.variant,
          );
          const relatedItems = cartItemsRef.current.filter((item) => item.product_id === change.productId);
          const previousVariantItem = change.variant
            ? relatedItems.find((item) => rowVariant(item) !== change.variant)
            : undefined;
          let brandCartId = isMagento ? magentoRegistry.cartIds.get(targetRetailerId) : undefined;
          if (isMagento) {
            console.info("[Magento cart] cart ID before sync", {
              hasBrandCartId: Boolean(brandCartId),
              creatingCart: !brandCartId,
            });
          }
          let externalItemId = currentItem?.external_item_id
            ? Number(currentItem.external_item_id)
            : undefined;
          const changeKey = `${targetRetailerId}:${change.productId}:${change.variant ?? ""}:${change.quantity}`;

          if (isMagento) {
            try {
              let completed = completedMagentoChangesRef.current.get(changeKey);
              if (!brandCartId) {
                brandCartId = await getOrCreateBrandMagentoCart(
                  magentoRegistry,
                  magentoCartCreateRef.current,
                  targetRetailerId,
                  session_token,
                );
              }

              if (!completed) {
                completed = { cartId: brandCartId, removedItemIds: new Set(), mutationDone: false };
                completedMagentoChangesRef.current.set(changeKey, completed);
              }
              if (previousVariantItem?.external_item_id && change.quantity > 0) {
                const oldItemId = String(previousVariantItem.external_item_id);
                if (!completed.removedItemIds.has(oldItemId)) {
                  await removeMagentoCartItem(targetRetailerId, brandCartId, Number(oldItemId), session_token);
                  completed.removedItemIds.add(oldItemId);
                }
              }
              if (!completed.mutationDone) {
                if (change.quantity === 0 && currentItem && externalItemId !== undefined) {
                  await removeMagentoCartItem(targetRetailerId, brandCartId, externalItemId, session_token);
                  completed.itemId = externalItemId;
                } else if (change.quantity > 0 && currentItem && externalItemId !== undefined) {
                  await updateMagentoCartItem(targetRetailerId, brandCartId, externalItemId, change.quantity, session_token);
                  completed.itemId = externalItemId;
                } else if (change.quantity > 0) {
                  if (!product.sku && !change.variant) throw new Error("We couldn't find the Magento product SKU.");
                  const added = await addMagentoCartItem({
                    retailerId: targetRetailerId,
                    cartId: brandCartId,
                    sku: product.sku ? product.sku : change.variant ? change.variant : "",
                    quantity: change.quantity,
                    selectedVariant: change.variant,
                  }, session_token);
                  completed.itemId = added.item_id;
                }
                completed.mutationDone = true;
              }
              brandCartId = completed.cartId;
              externalItemId = completed.itemId;
            } catch (error) {
              const message = error instanceof Error && error.message
                ? error.message
                : "The item couldn't be saved to the retailer's cart.";
              // A missing brand cart ID here means the Magento cart creation itself failed.
              if (!brandCartId) {
                trackCheckoutError(session_id, "magento_cart_creation", message, {
                  retailer_id: targetRetailerId,
                  product_id: change.productId,
                });
              }
              setCartSaveFailures((current) => {
                const key = cartRowKey(change.productId, change.variant);
                return [
                  ...current.filter((failure) => cartRowKey(failure.productId, failure.variant) !== key),
                  { productId: change.productId, variant: change.variant, message },
                ];
              });
              throw new MagentoCartSaveError(message, change.productId, change.variant);
            }
          }

          const response = await expressCartApi.updateCart(session_token, [
            {
              productId: change.productId,
              quantity: change.quantity,
              ...(change.variant ? { selectedSku: change.variant } : {}),
              // The checkout function reads externalCartId (it becomes the
              // RPC's p_external_cart_id, persisted to cart_items.brand_cart_id).
              ...(isMagento && brandCartId ? { externalCartId: brandCartId } : {}),
              ...(isMagento && brandCartId && externalItemId !== undefined
                ? { brand_cart_id: brandCartId, external_item_id: String(externalItemId) }
                : {}),
            },
          ]);
          cartItemsRef.current = response.cart.items;
          setCartRevision((revision) => revision + 1);
          if (!response.success) throw new Error(updateFailure(response.results[0]));
          // Track the cart change that just saved, via the external analytics API.
          const previousQuantity = currentItem?.quantity ?? 0;
          if (change.quantity > previousQuantity) {
            trackCartAdd(session_id, {
              product_id: change.productId,
              variant: change.variant ?? null,
              quantity: change.quantity,
              retailer_id: targetRetailerId,
            });
          } else if (change.quantity < previousQuantity) {
            trackCartRemove(session_id, {
              product_id: change.productId,
              variant: change.variant ?? null,
              quantity: change.quantity,
              retailer_id: targetRetailerId,
            });
          }
          completedMagentoChangesRef.current.delete(changeKey);
          if (isMagento) {
            setCartSaveFailures((current) =>
              current.filter((failure) =>
                failure.productId !== change.productId ||
                (failure.variant !== change.variant && change.quantity !== 0),
              ),
            );
          }
        }
        throw new Error("Your cart didn't update. Please try again.");
      });
      // Keep the chain alive after a failure; callers still see the error.
      syncChainRef.current = run.catch(() => undefined);
      return run;
    },
    [ensureSession, integrationType, link, magentoRegistry, retailerId],
  );

  useEffect(() => {
    // Magento brands only write at "Continue to checkout" (queueSync in
    // continueToCheckout): variant and quantity edits stay local until then,
    // and stop there if a save fails, or continue when they all succeed.
    if (isMagentoIntegration) return;
    // Without a session, wait for an edit so viewing a link creates nothing.
    if (!sessionRef.current && !editedRef.current) return;
    const timer = setTimeout(() => {
      queueSync(lines)
        .then(() => setSyncError(null))
        .catch((error: unknown) => {
          if (error instanceof MagentoCartSaveError) {
            setSyncError(null);
            return;
          }
          setSyncError(error instanceof Error && error.message
            ? `We couldn't save your cart: ${error.message}`
            : "We couldn't save your cart. Check your connection and try again.");
        });
    }, SYNC_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isMagentoIntegration, lines, queueSync]);

  const setLines = useCallback(
    (update: SetStateAction<CheckoutLine[]>) => {
      editedRef.current = true;
      setVerification(null);
      if (sessionRef.current && requiresVerification && !isMagentoIntegration) setIsVerificationPending(true);
      setLinesState((current) =>
        dropRemovedLines(link, typeof update === "function" ? update(current) : update),
      );
    },
    [link, requiresVerification, isMagentoIntegration],
  );

  const verifyCurrentCart = useCallback(async (): Promise<ProductVerificationResult | null> => {
    const currentSession = await ensureSession();
    const runId = ++verificationRunRef.current;
    setIsVerifying(true);
    try {
      const [config, marketplace] = await Promise.all([
        fetchIntegrationConfig(retailerId, currentSession.session_token),
        fetchMarketplaceSession(currentSession.session_id, currentSession.session_token),
      ]);
      const cartItems = cartItemsRef.current;
      const childRetailers = marketplace?.retailers ?? [];
      const isMarketplace = childRetailers.length > 0;
      const groups = buildProductVerificationGroups({
        isMarketplace,
        cartItemsByBrand: isMarketplace
          ? [...new Set(cartItems.map((item) => item.fulfillment_brand_id).filter((id): id is string => Boolean(id)))]
              .map((brandId) => [brandId, cartItems.filter((item) => item.fulfillment_brand_id === brandId)] as [string, CartItem[]])
          : [[retailerId, cartItems]],
        marketplaceRetailers: childRetailers,
        apiConfig: config,
        retailerId,
      });
      if (groups.length === 0) {
        const empty = { issues: [], stockLevels: [], priceIssues: [], failedRetailerIds: [] };
        if (runId === verificationRunRef.current) {
          setVerification(empty);
          setIsVerificationPending(false);
        }
        return empty;
      }
      const result = await verifyProductGroups(groups, undefined, currentSession.session_token);
      if (runId === verificationRunRef.current) {
        setVerification(result);
        setIsVerificationPending(false);
      }
      return result;
    } catch {
      const failed = {
        issues: [],
        stockLevels: [],
        priceIssues: [],
        failedRetailerIds: [retailerId],
      };
      if (runId === verificationRunRef.current) {
        setVerification(failed);
        setIsVerificationPending(false);
      }
      return failed;
    } finally {
      if (runId === verificationRunRef.current) setIsVerifying(false);
    }
  }, [ensureSession, retailerId]);

  const continueToCheckout = useCallback(async () => {
    if (magentoRegistry.conflictingBrands.size > 0) {
      throw new Error(MULTIPLE_BRAND_CARTS_ERROR);
    }
    await queueSync(lines);
    const { session_id, session_token } = await ensureSession();
    saveMagentoCartIds(session_id, Object.fromEntries(magentoRegistry.cartIds));
    if (requiresVerification && (!session || isMagentoIntegration)) {
      continueVerificationRef.current = true;
      setIsVerificationPending(true);
      try {
        const checked = await verifyCurrentCart();
        if (checked?.issues.length) {
          throw new Error("Some items are out of stock or exceed available stock. Update your cart to continue.");
        }
        if (checked?.failedRetailerIds.length) {
          window.alert("We couldn't confirm live stock for some items. You can continue, but stock will be checked before payment.");
        }
      } finally {
        continueVerificationRef.current = false;
      }
    } else if (verification?.issues.length) {
      throw new Error("Some items are out of stock or exceed available stock. Update your cart to continue.");
    }
    if (checkout === "shopify") {
      window.location.assign(await expressCartApi.getShopifyCheckoutUrl(session_token));
      return;
    }
    const sessionDetails = await expressCartApi.getSession(session_id, session_token);
    cacheSessionResponse(queryClient, session_id, sessionDetails);
    if (integrationType?.toLowerCase() === "shopify" || integrationType?.toLowerCase() === "marketplace") {
      const draftResponse = await draftApi.DraftOrder({
        session_id,
        retailer_id: sessionDetails.session.retailer_id,
        customer_email: sessionDetails.session.customer?.email ?? undefined,
      });
      if (draftOrderFailed(draftResponse)) {
        throw new Error("We couldn't prepare your order. Please try again.");
      }
      saveDraftDiscountHandoff(
        session_id,
        automaticDiscountsFromDraft(draftResponse),
      );
    }
    // "Back to cart" in checkout returns to this session's standalone cart.
    // A session exists now, so that's /cart?session= even when the shopper
    // started on a brand link (/cart/{slug}); keep its token for that page.
    saveSessionCartToken(session_id, session_token);
    const src = linkSource();
    saveExpressCartReturn(
      session_id,
      `/cart?session=${encodeURIComponent(session_id)}${src ? `&src=${encodeURIComponent(src)}` : ""}`,
    );
    // The route supplies the session ID; the standalone cart has already been reviewed.
    navigate(
      `/order/${encodeURIComponent(session_id)}?token=${encodeURIComponent(session_token)}`,
    );
  }, [checkout, ensureSession, integrationType, isMagentoIntegration, lines, magentoRegistry, navigate, queryClient, queueSync, requiresVerification, session, verification, verifyCurrentCart]);

  useEffect(() => {
    if (!activeSession || lines.length === 0 || !requiresVerification) return;
    if (isMagentoIntegration) return;
    let cancelled = false;
    setIsVerificationPending(true);
    const timer = window.setTimeout(() => {
      if (continueVerificationRef.current) return;
      void queueSync(lines)
        .then(() => {
          if (!cancelled) return verifyCurrentCart();
          return null;
        })
        .catch(() => {
          setIsVerificationPending(false);
          return null;
        });
    }, SYNC_DELAY_MS + 100);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [activeSession, isMagentoIntegration, lines, queueSync, requiresVerification, verifyCurrentCart]);

  const liveStockBySku = new Map(
    (verification?.stockLevels ?? []).map(({ item, stock }) => [`${item.product_id}:${item.sku}`, stock]),
  );
  const livePricesBySku = new Map(
    (verification?.priceIssues ?? []).map(({ item, price }) => [`${item.product_id}:${item.sku}`, price]),
  );
  const linesWithCartItemIds = useMemo(
    () => attachCartItemIds(link, lines, cartItemsRef.current),
    [cartRevision, lines, link],
  );
  return {
    lines: linesWithCartItemIds,
    setLines,
    isRestoring: false,
    continueToCheckout,
    syncError: cartSaveFailures.length > 0
      ? `We couldn't save your cart: ${cartSaveFailures[0]?.message ?? "Please try again."}`
      : syncError,
    cartSaveFailures,
    verification,
    isVerifying,
    isVerificationPending,
    requiresVerification,
    hasSession: Boolean(activeSession),
    liveStockBySku,
    livePricesBySku,
    session: activeSession,
  };
}
